import { useRouteContext } from '@tanstack/react-router';
import { useCallback } from 'react';

export interface AuthorizeFn {
  /**
   * 所需的权限，可以指定一个权限，也可以指定权限间且和或的关系
   *
   * @example
   *
   * 可以指定一个权限
   *
   * ```ts
   * const authority = 'read_a';
   * ```
   *
   * 代表用户必须拥有 `read_a` 权限，否则视为无权限
   *
   * @example
   *
   * 也可以指定一组权限，多个权限之间为或的关系，即拥有其中一个权限就视为有权限
   *
   * 例如有以下权限
   *
   * ```ts
   * const authorities = ['read_a', 'read_b'];
   * ```
   *
   * 代表当用户拥有 `read_a` 或 `read_b` 之一即可
   *
   * 等价于以下判断条件
   *
   * ```ts
   * const isAuthorized = is_read_a || is_read_b;
   * ```
   *
   * @example
   *
   * 还可以指定多个权限之间为且的关系，即使用 `string[][]` 的数据结构
   *
   * 例如有以下权限
   *
   * ```ts
   * const authorities = [['read_a', 'write_a']];
   * ```
   *
   * 代表当用户同时拥有 `read_a` 和 `write_a` 两个权限时才视为有权限
   *
   * 等价于以下判断条件
   *
   * ```ts
   * const isAuthorized = is_read_a && is_write_a;
   * ```
   *
   * @example
   *
   * 上面两种情况可以组合使用
   *
   * ```ts
   * const authorities = [['read_a', 'write_a'], 'read_b'];
   * ```
   *
   * 等价于以下的代码
   *
   * ```ts
   * const isAuthorized = (is_read_a && is_write_a) || is_read_b;
   * ```
   */
  (authorities?: string | string[] | (string[] | string)[]): boolean;
}

export const useAuthority = () => {
  const { permissions } = useRouteContext({ from: '/_creator-center' });

  const authorize = useCallback<AuthorizeFn>(
    (orAuthorities) => {
      // 没传权限的话代表不需要权限
      if (orAuthorities == null) {
        return true;
      }

      if (!Array.isArray(orAuthorities)) {
        return permissions.has(orAuthorities);
      }

      // 第一层权限数组内的权限为「或」的关系，即有个只要有一个权限满足即可
      return orAuthorities.some((andAuthorities) => {
        if (!Array.isArray(andAuthorities)) {
          return permissions.has(andAuthorities);
        }

        // 第二层的权限数组内的权限为「且」的关系，即必须满足所有权限要求
        return andAuthorities.every((andAuthority) =>
          permissions.has(andAuthority),
        );
      });
    },
    [permissions],
  );

  return authorize;
};
